/**
 * 🔌 SERVICIOS API FRONTEND - INTEGRACIÓN COMPLETA BACKEND
 * Sistema de servicios para exponer todas las funcionalidades del backend
 * Paso 1.3: Conectores API que exponen 100% del backend
 */

import { ApiClient, type ApiResponse } from './api-client';
import { measureApi } from '../utils/measureApi';
import { extractInventoryError } from '../errorMaps/inventory';
import type {
  UserRole, IncidenceStatus, IncidencePriority,
  User, Incidence, Agency, InventoryItem,
  IncidenceFilters, UserFilters, AgencyFilters, InventoryFilters,
  KPIMetric, AnalyticsReport, Prediction,
  RoutingAlgorithm, WeatherData, TrafficData,
  CreateIncidenceData, UpdateIncidenceData, CreateUserData,
  CreateAgencyData, CreateInventoryItemData,
  Location, AgencyEvent
} from '@/types/enterprise';

/**
 * 🚨 INCIDENCE SERVICE - Ciclo Completo
 * Servicio para gestión completa de incidencias con todos los campos del backend
 */
export class EnterpriseIncidenceService {
  static baseUrl = '/api/incidences';

  // P13-V1D (C10, R0-INC-09): los métodos PHANTOM (priority/timeline/assign/reassign/auto-assign/
  // escalate/location/geo/feedback/analytics/sla/resolution-time/performance — rutas que el backend
  // nunca sirvió) fueron RETIRADOS; sobreviven solo los métodos con rutas reales.

  // CRUD Básico con ciclo completo
  static async getAll(filters: IncidenceFilters = {}): Promise<ApiResponse<Incidence[]>> {
    return ApiClient.get(this.baseUrl, { params: filters });
  }

  static async create(incidenceData: CreateIncidenceData): Promise<ApiResponse<Incidence>> {
    return ApiClient.post(this.baseUrl, incidenceData);
  }

  static async getById(id: string): Promise<ApiResponse<Incidence>> {
    return ApiClient.get(`${this.baseUrl}/${id}`);
  }

  static async update(id: string, data: UpdateIncidenceData): Promise<ApiResponse<Incidence>> {
    return ApiClient.put(`${this.baseUrl}/${id}`, data);
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return ApiClient.delete(`${this.baseUrl}/${id}`);
  }

  // Estado y prioridad
  static async updateStatus(id: string, status: IncidenceStatus): Promise<ApiResponse<Incidence>> {
    return ApiClient.put(`${this.baseUrl}/${id}/status`, { status });
  }

  static async getHistory(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/history`);
  }

  // Attachments y evidencias (P12 post-Gate-A: campo multipart canónico 'files')
  static async uploadAttachment(id: string, file: File): Promise<ApiResponse<any>> {
    const { attachmentService } = await import('@/services/api/attachment.service');
    const { newUploadIdempotencyKey } = await import('@/services/api/uploadIdempotencyKey');
    // P13-V5B O1: cada llamada es UNA acción de subida del usuario ⇒ una clave nueva por llamada.
    const attachment = await attachmentService.upload(
      { kind: 'incidence', id: Number(id) },
      file,
      { onProgress: () => undefined, idempotencyKey: newUploadIdempotencyKey() }
    );
    return { data: attachment, status: 201 } as ApiResponse<any>;
  }

  static async getAttachments(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/attachments`);
  }

  static async deleteAttachment(id: string, attachmentId: string): Promise<ApiResponse<void>> {
    return ApiClient.delete(`${this.baseUrl}/${id}/attachments/${attachmentId}`);
  }

  // Comentarios y feedback
  static async addComment(id: string, comment: string): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/${id}/comments`, { comment });
  }

  static async getComments(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/comments`);
  }

}

/**
 * 👥 USER SERVICE - Performance Tracking Completo
 * Gestión completa de usuarios con performance metrics y skills
 */
export class EnterpriseUserService {
  static baseUrl = '/api/users';

  // CRUD con perfiles completos
  static async getAll(filters: UserFilters = {}): Promise<ApiResponse<User[]>> {
    return ApiClient.get(this.baseUrl, { params: filters });
  }

  static async create(userData: CreateUserData): Promise<ApiResponse<User>> {
    return ApiClient.post(this.baseUrl, userData);
  }

  static async getById(id: string): Promise<ApiResponse<User>> {
    return ApiClient.get(`${this.baseUrl}/${id}`);
  }

  static async update(id: string, data: Partial<User>): Promise<ApiResponse<User>> {
    return ApiClient.put(`${this.baseUrl}/${id}`, data);
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return ApiClient.delete(`${this.baseUrl}/${id}`);
  }

  // Performance y métricas
  static async getPerformance(id: string): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/${id}/performance`);
  }

  static async getWorkload(id: string): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/${id}/workload`);
  }

  static async updateWorkload(id: string, workloadData: any): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/${id}/workload`, workloadData);
  }

  static async getAvailability(id: string): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/${id}/availability`);
  }

  static async updateAvailability(id: string, availabilityData: any): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/${id}/availability`, availabilityData);
  }

  // Skills y competencias
  static async getSkills(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/skills`);
  }

  static async updateSkills(id: string, skills: any[]): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/${id}/skills`, { skills });
  }

  static async evaluate(id: string, evaluation: any): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/${id}/evaluate`, evaluation);
  }

  static async getEvaluations(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/evaluations`);
  }

  // Jerarquías y asignaciones
  static async getHierarchy(id: string): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/${id}/hierarchy`);
  }

  static async assignSupervisor(id: string, supervisorId: string): Promise<ApiResponse<User>> {
    return ApiClient.put(`${this.baseUrl}/${id}/assign-supervisor`, { supervisorId });
  }

  static async getSubordinates(id: string): Promise<ApiResponse<User[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/subordinates`);
  }

  static async getAssignments(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/assignments`);
  }

  // Analytics de usuarios
  static async getPerformanceAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/analytics/performance`);
  }

  static async getWorkloadAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/analytics/workload`);
  }

  static async getSkillsAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/analytics/skills`);
  }
}

/**
 * 📦 INVENTORY SERVICE - Predictive Analytics
 * Sistema completo de inventario con ML y analytics predictivos
 *
 * PR-31 — Servicio SECUNDARIO para Inventory (solo reports)
 * Los únicos métodos con consumidores activos son los 4 de reports:
 *   getExecutiveReport, getAlertsReport (categories/movements RETIRADOS — mocks 410)
 * Consumidores: prefetch.ts, RealtimeReports.vue
 *
 * El servicio CANÓNICO del agregado Inventory es inventoryService.ts (PR-31).
 * El grueso de los métodos de esta clase (CRUD, stock, predicciones, ML,
 * proveedores, distribución) NO tiene consumidores frontend activos.
 */
export class EnterpriseInventoryService {
  static baseUrl = '/api/inventory';

  // CRUD básico
  static async getAll(filters: InventoryFilters = {}): Promise<ApiResponse<InventoryItem[]>> {
    try {
      return await measureApi(() => ApiClient.get(this.baseUrl, { params: filters }), { label: 'enterpriseInventory.list', meta: { filters: Object.keys(filters).length } });
    } catch (e:any) { throw extractInventoryError(e) }
  }

  static async create(itemData: CreateInventoryItemData): Promise<ApiResponse<InventoryItem>> {
    try { return await measureApi(() => ApiClient.post(this.baseUrl, itemData), { label: 'enterpriseInventory.create' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getById(id: string): Promise<ApiResponse<InventoryItem>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/${id}`), { label: 'enterpriseInventory.get', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async update(id: string, data: Partial<InventoryItem>): Promise<ApiResponse<InventoryItem>> {
    try { return await measureApi(() => ApiClient.put(`${this.baseUrl}/${id}`, data), { label: 'enterpriseInventory.update', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    try { return await measureApi(() => ApiClient.delete(`${this.baseUrl}/${id}`), { label: 'enterpriseInventory.delete', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // Stock y movimientos
  static async getStockLevels(): Promise<ApiResponse<any[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/stock-levels`), { label: 'enterpriseInventory.stockLevels' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async updateStock(id: string, quantity: number, reason: string): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.put(`${this.baseUrl}/${id}/stock`, { quantity, reason }), { label: 'enterpriseInventory.updateStock', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getMovements(id: string): Promise<ApiResponse<any[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/${id}/movements`), { label: 'enterpriseInventory.movements', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async recordMovement(movementData: any): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.post(`${this.baseUrl}/movement`, movementData), { label: 'enterpriseInventory.recordMovement' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // Predicciones y ML
  static async getPredictions(): Promise<ApiResponse<Prediction[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/predictions`), { label: 'enterpriseInventory.predictions' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getItemPrediction(id: string): Promise<ApiResponse<Prediction>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/predictions/${id}`), { label: 'enterpriseInventory.prediction', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async generatePredictions(items: string[] = []): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.post(`${this.baseUrl}/predictions/generate`, { items }), { label: 'enterpriseInventory.generatePredictions', meta: { count: items.length } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getForecasting(period = '30d'): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/forecasting`, { params: { period } }), { label: 'enterpriseInventory.forecasting', meta: { period } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // Alertas y reorder
  static async getAlerts(): Promise<ApiResponse<any[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/alerts`), { label: 'enterpriseInventory.alerts' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async setReorderPoint(id: string, point: number): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.put(`${this.baseUrl}/${id}/reorder-point`, { point }), { label: 'enterpriseInventory.setReorder', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async autoReorder(criteria: Record<string, any> = {}): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.post(`${this.baseUrl}/auto-reorder`, criteria), { label: 'enterpriseInventory.autoReorder' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getReorderSuggestions(): Promise<ApiResponse<any[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/reorder-suggestions`), { label: 'enterpriseInventory.reorderSuggestions' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // Proveedores y costos
  static async getSuppliers(): Promise<ApiResponse<any[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/suppliers`), { label: 'enterpriseInventory.suppliers' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getItemSuppliers(id: string): Promise<ApiResponse<any[]>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/${id}/suppliers`), { label: 'enterpriseInventory.itemSuppliers', meta: { id } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getSupplierPerformance(supplierId: string): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/suppliers/${supplierId}/performance`), { label: 'enterpriseInventory.supplierPerformance', meta: { supplierId } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getCostAnalysis(): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/cost-analysis`), { label: 'enterpriseInventory.costAnalysis' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getCostTrends(): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/cost-trends`), { label: 'enterpriseInventory.costTrends' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // Distribución
  static async distribute(distributionPlan: any): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.put(`${this.baseUrl}/distribute`, distributionPlan), { label: 'enterpriseInventory.distribute' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getDistributionAnalytics(): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/distribution-analytics`), { label: 'enterpriseInventory.distributionAnalytics' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async createDistributionPlan(planData: any): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.post(`${this.baseUrl}/distribution-plan`, planData), { label: 'enterpriseInventory.createDistributionPlan' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getDistributionEfficiency(): Promise<ApiResponse<any>> {
    try { return await measureApi(() => ApiClient.get(`${this.baseUrl}/distribution-efficiency`), { label: 'enterpriseInventory.distributionEfficiency' }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // -----------------------------
  // Reports (Enterprise 7.1)
  // -----------------------------
  static async getExecutiveReport(params: { period?: string; includeInactive?: boolean } = {}): Promise<ApiResponse<any>> {
    const { period = '30d', includeInactive = false } = params;
    try { return await measureApi(() => ApiClient.get(`/inventory/reports/executive`, { params: { period, includeInactive } }), { label: 'enterpriseInventory.report.executive', meta: { period } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  static async getAlertsReport(params: { resolved?: boolean; severity?: string } = {}): Promise<ApiResponse<any>> {
    const { resolved = false, severity } = params;
    try { return await measureApi(() => ApiClient.get(`/inventory/reports/alerts`, { params: { resolved, severity } }), { label: 'enterpriseInventory.report.alerts', meta: { resolved, severity: !!severity } }); } catch (e:any){ throw extractInventoryError(e) }
  }

  // P13-V2B (cierre terminal — LEGACY_RETIREMENT §6): getCategoriesReport y
  // getMovementsReport RETIRADOS — sus rutas backend servían datos SIMULADOS como reporte
  // operativo y ahora responden 410 (INVENTORY_REPORT_CATEGORIES_RETIRED /
  // INVENTORY_REPORT_MOVEMENTS_RETIRED). Ningún consumidor de producto debe prometer esas
  // capacidades; los reportes REALES son getExecutiveReport y getAlertsReport.
}

/**
 * 📊 ANALYTICS SERVICE - Business Intelligence Completo
 * Analytics avanzados con BI y predicciones
 */
export class EnterpriseAnalyticsService {
  static baseUrl = '/analytics';

  // KPIs y métricas
  static async getKPIs(role: UserRole | null = null): Promise<ApiResponse<KPIMetric[]>> {
    const url = role ? `${this.baseUrl}/kpis/${role}` : `${this.baseUrl}/kpis`;
    return ApiClient.get(url);
  }

  static async getRealtimeMetrics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/metrics/realtime`);
  }

  static async getPerformanceDashboard(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/performance-dashboard`);
  }

  // Trends y comparativas
  static async getTrends(period = '30d'): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/trends/${period}`);
  }

  static async getComparative(type = 'regions'): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/comparative/${type}`);
  }

  // Heat maps y visualización
  static async getHeatmaps(type = 'activity'): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/heatmaps/${type}`);
  }

  static async getVisualizations(chartType: string): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/visualizations`, { 
      params: { type: chartType } 
    });
  }

  // Predicciones y ML
  static async getPredictions(type = 'general'): Promise<ApiResponse<Prediction[]>> {
    return ApiClient.get(`${this.baseUrl}/predictions/${type}`);
  }

  static async getForecasting(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/forecasting`);
  }

  static async getPredictiveAnalytics(horizonte = 14, historial = 30): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/predictive`, {
      params: { horizonte, historial }
    });
  }

  static async getTrendSeries(months = 6): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/trends`, {
      params: { months }
    });
  }

  // Recomendaciones
  static async getRecommendations(type = 'optimization'): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/recommendations/${type}`);
  }

  // Reportes ejecutivos
  static async getExecutiveAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/executive`);
  }

  static async getExecutiveSummary(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/executive/summary`);
  }

  static async generateReport(reportConfig: any): Promise<ApiResponse<AnalyticsReport>> {
    return ApiClient.post(`${this.baseUrl}/reports/generate`, reportConfig);
  }

  static async getScheduledReports(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/reports/scheduled`);
  }
}

/**
 * 🏢 AGENCY SERVICE - Eventos y Calendar Integration
 * Gestión completa de agencias con eventos y estados estructurales
 */
export class EnterpriseAgencyService {
  static baseUrl = '/api/agencies';

  // CRUD básico
  static async getAll(filters: AgencyFilters = {}): Promise<ApiResponse<Agency[]>> {
    return ApiClient.get(this.baseUrl, { params: filters });
  }

  static async create(agencyData: CreateAgencyData): Promise<ApiResponse<Agency>> {
    return ApiClient.post(this.baseUrl, agencyData);
  }

  static async getById(id: string): Promise<ApiResponse<Agency>> {
    return ApiClient.get(`${this.baseUrl}/${id}`);
  }

  static async update(id: string, data: Partial<Agency>): Promise<ApiResponse<Agency>> {
    return ApiClient.put(`${this.baseUrl}/${id}`, data);
  }

  static async delete(id: string): Promise<ApiResponse<void>> {
    return ApiClient.delete(`${this.baseUrl}/${id}`);
  }

  // Estados estructurales
  static async getStates(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/states`);
  }

  static async updateStates(id: string, states: any[]): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/${id}/states`, states);
  }

  static async getMaintenanceHistory(id: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/maintenance-history`);
  }

  static async getRequiringAttention(): Promise<ApiResponse<Agency[]>> {
    return ApiClient.get(`${this.baseUrl}/requiring-attention`);
  }

  // Sistema de eventos
  static async getEvents(id: string): Promise<ApiResponse<AgencyEvent[]>> {
    return ApiClient.get(`${this.baseUrl}/${id}/events`);
  }

  static async createEvent(id: string, eventData: any): Promise<ApiResponse<AgencyEvent>> {
    return ApiClient.post(`${this.baseUrl}/${id}/events`, eventData);
  }

  static async updateEvent(eventId: string, data: any): Promise<ApiResponse<AgencyEvent>> {
    return ApiClient.put(`${this.baseUrl}/events/${eventId}`, data);
  }

  static async deleteEvent(eventId: string): Promise<ApiResponse<void>> {
    return ApiClient.delete(`${this.baseUrl}/events/${eventId}`);
  }

  static async completeEvent(eventId: string, completionData: any): Promise<ApiResponse<AgencyEvent>> {
    return ApiClient.put(`${this.baseUrl}/events/${eventId}/complete`, completionData);
  }

  // Calendar integration
  static async getCalendar(supervisorId: string | null = null): Promise<ApiResponse<any[]>> {
    const url = supervisorId 
      ? `${this.baseUrl}/events/calendar/${supervisorId}`
      : `${this.baseUrl}/events/calendar`;
    return ApiClient.get(url);
  }

  static async getUpcomingEvents(): Promise<ApiResponse<AgencyEvent[]>> {
    return ApiClient.get(`${this.baseUrl}/events/upcoming`);
  }

  static async getOverdueEvents(): Promise<ApiResponse<AgencyEvent[]>> {
    return ApiClient.get(`${this.baseUrl}/events/overdue`);
  }

  // P13-V4 — the "tracking y validación" methods (proximity check, visit history, visit validation) are
  // RETIRED: phantom contracts the backend never served, and a second visit/GPS writer shape outside the
  // canonical route-execution chain (visits live in /api/execution-visits).

  // Analytics
  static async getPerformanceAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/analytics/performance`);
  }

  static async getMaintenanceAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/analytics/maintenance`);
  }

  static async getComplianceAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/analytics/compliance`);
  }
}

/**
 * 🗺️ MAPS SERVICE - Geospatial Analytics
 * Servicio para mapas interactivos con capas múltiples
 */
export class EnterpriseMapsService {
  static baseUrl = '/api';

  // P13-V4 (R0-BFF-06) — the position methods (stream / current / history / update) are RETIRED:
  // phantom endpoints the backend never served. A position is written only through the canonical,
  // execution-bound GPS writer or the canonical supervisor-position store.

  // Datos para mapas

  static async getAgenciesMapData(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/agencies/map-data`);
  }


  // Clustering y analytics
  static async getClusters(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/geo/clusters`);
  }

  static async getHeatmapData(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/geo/heatmap`);
  }

  static async getGeoAnalytics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/geo/analytics`);
  }

  static async getZones(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/geo/zones`);
  }

  // Filtros geográficos

  static async filterAgenciesByRegion(regionId: string): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/geo/filter/agencies`, {
      params: { region: regionId }
    });
  }

  static async getNearby(lat: number, lng: number, radius = 1000): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/geo/nearby/${lat}/${lng}`, {
      params: { radius }
    });
  }
}

/**
 * 🌤️ EXTERNAL SERVICES - Weather & Traffic Integration
 * Servicios externos integrados para optimización operacional
 */
export class EnterpriseExternalService {
  static baseUrl = '/api/external';

  // Servicios meteorológicos
  static async getCurrentWeather(): Promise<ApiResponse<WeatherData>> {
    return ApiClient.get(`${this.baseUrl}/weather/current`);
  }

  static async getWeatherForecast(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/weather/forecast`);
  }

  static async getWeatherAlerts(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/weather/alerts`);
  }

  static async analyzeWeatherImpact(operationalData: any): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/weather/impact-analysis`, operationalData);
  }

  // Servicios de tráfico
  static async getCurrentTraffic(): Promise<ApiResponse<TrafficData>> {
    return ApiClient.get(`${this.baseUrl}/traffic/current`);
  }

  static async analyzeTraffic(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/traffic/analyze`);
  }

  static async optimizeRoute(routeData: any): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/traffic/optimize-route`, routeData);
  }

  static async getTrafficIncidents(): Promise<ApiResponse<any[]>> {
    return ApiClient.get(`${this.baseUrl}/traffic/incidents`);
  }

  // Integración de mapas
  static async optimizeWithGoogleMaps(routeData: any): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/maps/optimize-route`, routeData);
  }

  static async geocode(address: string): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/maps/geocode`, {
      params: { address }
    });
  }

  static async reverseGeocode(lat: number, lng: number): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/maps/reverse-geocode`, {
      params: { lat, lng }
    });
  }

  // Analytics y configuración
  static async getOperationalImpact(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/operational-impact`);
  }

  static async getServiceStatus(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/config/status`);
  }

  static async updateAPIKeys(keyData: any): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/config/keys`, keyData);
  }
}

/**
 * 🔄 ROUTING SERVICE - Intelligent Assignment
 * Sistema de routing automático y asignación inteligente
 *
 * ⚠️ @deprecated G04-FE (H-A) — CUARENTENA: NO CABLEAR SIN REALINEAR PRIMERO.
 * Estos endpoints NO existen en el backend canónico. El backend de routing
 * (src/routes/routing.js) expone rutas con la forma `/:administrationId/{route,
 * reoptimize,optimize-weights,config,...}` + `/templates`, todas bajo
 * authenticate → authorizeRoles([super_admin, administration_manager, group_manager])
 * → enforceScope → validación de `:administrationId`.
 * En cambio, esta clase apunta a `/auto-assign`, `/auto-assign/batch`, `/reassign`,
 * `/rebalance`, `/algorithms`, `/optimize`, `/performance`, etc., que NO están montados.
 * Actualmente NO tiene consumidores activos (los únicos usos en
 * RouteOperatorDashboard.vue / RouteOperatorDashboard_new.vue están comentados).
 * Antes de habilitar cualquier UI de routing, realinear paths/firmas al contrato real
 * y respetar el parámetro de alcance `:administrationId` (la autoridad es del backend).
 */
export class EnterpriseRoutingService {
  // ⚠️ Drift conocido vs backend (ver banner @deprecated arriba). No usar tal cual.
  static baseUrl = '/api/routing';

  // Asignación automática
  static async autoAssign(criteria: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/auto-assign`, criteria);
  }

  static async autoAssignBatch(incidenceIds: string[]): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/auto-assign/batch`, { incidenceIds });
  }

  static async getAlgorithms(): Promise<ApiResponse<RoutingAlgorithm[]>> {
    return ApiClient.get(`${this.baseUrl}/algorithms`);
  }

  static async configureAlgorithm(algorithmConfig: any): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/algorithm/configure`, algorithmConfig);
  }

  // Rebalancing y optimización
  static async reassign(reassignmentData: any): Promise<ApiResponse<any>> {
    return ApiClient.put(`${this.baseUrl}/reassign`, reassignmentData);
  }

  static async rebalance(criteria: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/rebalance`, criteria);
  }

  static async getWorkloadAnalysis(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/workload-analysis`);
  }

  static async optimize(optimizationCriteria: any): Promise<ApiResponse<any>> {
    return ApiClient.post(`${this.baseUrl}/optimize`, optimizationCriteria);
  }

  // Performance y métricas
  static async getPerformance(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/performance`);
  }

  static async getMetrics(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/metrics`);
  }

  static async getEfficiency(): Promise<ApiResponse<any>> {
    return ApiClient.get(`${this.baseUrl}/efficiency`);
  }

  // P13-V8 (DEC-V8-07): getSLACompliance() is retired — no SLA metric is delivered or advertised.
}

/**
 * 🎯 ROLE-BASED API ACCESS
 * Control de acceso a APIs basado en roles empresariales
 */
export class RoleBasedAPIAccess {
  // Tipo de clases de servicio permitidas (heterogéneas)
  static getAvailableServices(userRole: UserRole): (
    | typeof EnterpriseIncidenceService
    | typeof EnterpriseUserService
    | typeof EnterpriseInventoryService
    | typeof EnterpriseAnalyticsService
    | typeof EnterpriseAgencyService
    | typeof EnterpriseMapsService
    | typeof EnterpriseExternalService
    | typeof EnterpriseRoutingService
  )[] {
    const serviceMap: Record<
      UserRole,
      (
        | typeof EnterpriseIncidenceService
        | typeof EnterpriseUserService
        | typeof EnterpriseInventoryService
        | typeof EnterpriseAnalyticsService
        | typeof EnterpriseAgencyService
        | typeof EnterpriseMapsService
        | typeof EnterpriseExternalService
        | typeof EnterpriseRoutingService
      )[]
    > = {
      'super_admin': [
        EnterpriseIncidenceService, EnterpriseUserService, EnterpriseInventoryService, 
        EnterpriseAnalyticsService, EnterpriseAgencyService, EnterpriseMapsService, 
        EnterpriseExternalService, EnterpriseRoutingService
      ],
      'administration_manager': [
        EnterpriseUserService, EnterpriseInventoryService, EnterpriseAgencyService
      ],
      'group_manager': [
        EnterpriseIncidenceService, EnterpriseUserService, EnterpriseInventoryService, 
        EnterpriseAnalyticsService, EnterpriseAgencyService
      ],
      'field_supervisor': [
        EnterpriseIncidenceService, EnterpriseUserService, EnterpriseMapsService, 
        EnterpriseRoutingService
      ],
      'route_operator': [
        EnterpriseIncidenceService, EnterpriseMapsService, EnterpriseExternalService
      ],
      'agency_employee': [
        EnterpriseIncidenceService, EnterpriseAgencyService
      ],
      'department_head': [
        EnterpriseAnalyticsService, EnterpriseUserService, EnterpriseIncidenceService
      ]
    };

    return serviceMap[userRole] || [];
  }

  static canAccessService(userRole: UserRole, serviceName: string): boolean {
    const availableServices = this.getAvailableServices(userRole);
    return availableServices.some(service => service.name === serviceName);
  }
}

// Exportar todos los servicios
export {
  EnterpriseIncidenceService as IncidenceService,
  EnterpriseUserService as UserService,
  EnterpriseInventoryService as InventoryService,
  EnterpriseAnalyticsService as AnalyticsService,
  EnterpriseAgencyService as AgencyService,
  EnterpriseMapsService as MapsService,
  EnterpriseExternalService as ExternalService,
  EnterpriseRoutingService as RoutingService,
};

export default {
  IncidenceService: EnterpriseIncidenceService,
  UserService: EnterpriseUserService,
  InventoryService: EnterpriseInventoryService,
  AnalyticsService: EnterpriseAnalyticsService,
  AgencyService: EnterpriseAgencyService,
  MapsService: EnterpriseMapsService,
  ExternalService: EnterpriseExternalService,
  RoutingService: EnterpriseRoutingService,
};
